<script setup lang="ts">
const props = withDefaults(defineProps<{
  name?: string;
  url?: string;
}>(), {
  name: 'Donoor',
  url: 'https://donoor.org/pay/donoor',
});

const { t } = useI18n();
const isExpanded = ref(false);
/**
 * Déplié au SURVOL, avec une vraie souris seulement. Sur un écran tactile, un
 * toucher déclenche aussi `mouseenter` (et le garde) : la carte restait
 * dépliée sur toute la largeur, sous la bulle du chat (Karim 2026-10-15).
 */
function deplier(ouvert: boolean) {
  if (ouvert && !window.matchMedia("(hover: hover)").matches) return;
  isExpanded.value = ouvert;
}
const heroExamplesOpen = useState('heroExamplesOpen', () => false);
const isCustom = computed(() => props.name !== 'Donoor');
</script>

<template>
  <div
    class="support-wrapper"
    :class="{ 'is-expanded': isExpanded, 'is-hidden': heroExamplesOpen }"
    @mouseenter="deplier(true)"
    @mouseleave="deplier(false)"
  >
    <!-- Entire card is clickable -->
    <NuxtLink :to="props.url" target="_blank" external class="support-card">
      <!-- Illustration (visible only when expanded) -->
      <div class="support-illustration">
        <img
          src="/svg/hands-heart-2.svg"
          width="315"
          height="161"
          alt=""
          class="support-illustration-img"
          aria-hidden="true"
        />
      </div>

      <!-- Right side content -->
      <div class="support-right">
        <!-- Expanded content -->
        <div class="support-expanded-content">
          <p class="support-headline">
            {{ t('ui.supportDonoor.titreLigne1') }}<br />{{ t('ui.supportDonoor.titreLigne2') }}
          </p>
          <span class="support-learn-more">
            {{ t('ui.supportDonoor.enSavoirPlus') }}
          </span>
        </div>

        <!-- CTA row (always visible, morphs between small/large) -->
        <div class="support-cta">
          <div class="support-cta-text">
            <span class="support-cta-label">
              {{ isExpanded ? t('ui.supportDonoor.jeSoutiens') : t('ui.supportDonoor.soutenir') }}
            </span>
            <span class="support-cta-brand" :class="{ 'support-cta-brand--custom': isCustom }">{{ props.name }}</span>
          </div>
          <!-- Heart: gold outline when expanded, SVG illustration when collapsed -->
          <img
            src="/svg/hands-heart-2-red.svg"
          width="315"
          height="161"
            alt=""
            class="support-cta-heart-img"
            :class="{ 'hidden': isExpanded }"
            aria-hidden="true"
          />
          <svg
            v-show="isExpanded"
            class="support-cta-heart"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              stroke="#D4A843"
              stroke-width="1.8"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>
    </NuxtLink>
  </div>
</template>

<style scoped>
/* ==================== WRAPPER ==================== */
/* En bas à GAUCHE : le coin bas-droit est celui du chat (PublicChatWidget),
   qui recouvrait ce bouton sur les pages d'association (Karim 2026-10-15). */
.support-wrapper {
  position: fixed;
  bottom: 1.5rem;
  left: 1.5rem;
  z-index: 50;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.support-wrapper.is-hidden {
  opacity: 0;
  pointer-events: none;
  transform: translateY(1rem);
}

/* ==================== CARD (morphs between states) ==================== */
.support-card {
  display: flex;
  align-items: stretch;
  background: #131445;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  transition:
    box-shadow 0.4s cubic-bezier(0.25, 0.1, 0.25, 1),
    border-radius 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.is-expanded .support-card {
  border-radius: 24px;
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.3);
}

/* ==================== ILLUSTRATION ==================== */
.support-illustration {
  width: 0;
  overflow: hidden;
  background: #f0f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition:
    width 0.4s cubic-bezier(0.25, 0.1, 0.25, 1),
    opacity 0.4s cubic-bezier(0.25, 0.1, 0.25, 1),
    padding 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
  padding: 0;
}

.is-expanded .support-illustration {
  width: 120px;
  opacity: 1;
  padding: 0.75rem;
}

.support-illustration-img {
  width: 90px;
  height: auto;
  flex-shrink: 0;
}

/* ==================== RIGHT SIDE ==================== */
.support-right {
  display: flex;
  flex-direction: column;
  padding: 14px 18px;
  transition: padding 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.is-expanded .support-right {
  padding: 1.25rem 1.5rem;
}

/* ==================== EXPANDED CONTENT ==================== */
.support-expanded-content {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transition:
    max-height 0.4s cubic-bezier(0.25, 0.1, 0.25, 1),
    opacity 0.35s cubic-bezier(0.25, 0.1, 0.25, 1),
    margin 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
  margin-bottom: 0;
}

.is-expanded .support-expanded-content {
  max-height: 120px;
  opacity: 1;
  margin-bottom: 0.75rem;
}

.support-headline {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.3;
  margin-bottom: 0.25rem;
}

.support-learn-more {
  font-size: 11px;
  font-style: italic;
  font-weight: 600;
  color: #ffffff;
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* ==================== CTA ROW ==================== */
.support-cta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
}

.support-cta-text {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.support-cta-label {
  font-size: 16px;
  font-style: italic;
  font-weight: 600;
  color: #D4A843;
  transition: font-size 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.is-expanded .support-cta-label {
  font-size: 17px;
}

.support-cta-brand {
  font-size: 20px;
  font-weight: 800 !important;
  color: #ffffff;
  letter-spacing: 0.5px;
  transition: font-size 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.is-expanded .support-cta-brand {
  font-size: 22px;
}

.support-cta-brand--custom {
  font-size: 13px;
  letter-spacing: 0.2px;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.is-expanded .support-cta-brand--custom {
  font-size: 15px;
  max-width: 200px;
}

.support-cta-heart {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  transition:
    width 0.4s cubic-bezier(0.25, 0.1, 0.25, 1),
    height 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.is-expanded .support-cta-heart {
  width: 40px;
  height: 40px;
}

.support-cta-heart-img {
  width: 50px;
  height: 50px;
  flex-shrink: 0;
  margin-left: 1rem;
}

/* ==================== MOBILE ==================== */
@media (max-width: 639px) {
  .support-wrapper {
    bottom: 1rem;
    left: 1rem;
  }

  .support-right {
    padding: 10px 14px;
  }

  .support-cta-label {
    font-size: 14px;
  }

  .support-cta-brand {
    font-size: 18px;
  }

  .support-cta-heart {
    width: 28px;
    height: 28px;
  }

  .support-cta-heart-img {
    width: 40px;
    height: 40px;
    margin-left: 0.75rem;
  }

  .is-expanded .support-illustration {
    width: 100px;
    padding: 0.5rem;
  }

  .support-illustration-img {
    width: 80px;
  }

  .is-expanded .support-right {
    padding: 1rem;
  }

  .support-headline {
    font-size: 12px;
  }

  .is-expanded .support-cta-label {
    font-size: 11px;
  }

  .is-expanded .support-cta-brand {
    font-size: 15px;
  }

  .is-expanded .support-cta-heart {
    width: 28px;
    height: 28px;
  }
}
</style>
